import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useTournament } from "@/lib/TournamentContext";
import { toast } from "sonner";
import { Star } from "lucide-react";

const POSITIONS = ["GK", "DEF", "MID", "FWD"];

export default function Fantasy() {
  const { active } = useTournament();
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState({});
  const [squad, setSquad] = useState([]);
  const [captain, setCaptain] = useState(null);
  const [posFilter, setPosFilter] = useState("ALL");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!active) return;
    (async () => {
      const tid = active.tournament_id;
      const [pR, tR, sR] = await Promise.all([
        api.get(`/tournaments/${tid}/fantasy/players`),
        api.get(`/tournaments/${tid}/teams`),
        api.get(`/tournaments/${tid}/fantasy/me`),
      ]);
      setPlayers(pR.data);
      setTeams(Object.fromEntries(tR.data.map((t) => [t.team_id, t])));
      setSquad(sR.data.player_ids || []);
      setCaptain(sR.data.captain_id || null);
    })();
  }, [active]);

  const cfg = active?.config;
  const playerMap = useMemo(() => Object.fromEntries(players.map((p) => [p.player_id, p])), [players]);
  const squadPlayers = squad.map((id) => playerMap[id]).filter(Boolean);
  const totalValue = squadPlayers.reduce((s, p) => s + p.value, 0);
  const budgetLeft = (cfg?.fantasy_budget || 0) - totalValue;
  const nationCounts = squadPlayers.reduce((acc, p) => { acc[p.team_id] = (acc[p.team_id] || 0) + 1; return acc; }, {});

  const togglePlayer = (id) => {
    if (squad.includes(id)) {
      setSquad((s) => s.filter((x) => x !== id));
      if (captain === id) setCaptain(null);
      return;
    }
    if (squad.length >= 11) { toast.error("Squad full (11)"); return; }
    const p = playerMap[id];
    if (totalValue + p.value > cfg.fantasy_budget) { toast.error("Over budget"); return; }
    if ((nationCounts[p.team_id] || 0) >= cfg.max_per_nation) { toast.error(`Max ${cfg.max_per_nation} per nation`); return; }
    setSquad((s) => [...s, id]);
  };

  const save = async () => {
    try {
      await api.post(`/tournaments/${active.tournament_id}/fantasy`, { player_ids: squad, captain_id: captain });
      toast.success("Squad saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const filtered = players.filter((p) => {
    if (posFilter !== "ALL" && p.position !== posFilter) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (!active) return null;

  return (
    <div className="px-3 md:px-8 py-6 md:py-8 grid-bg min-h-[calc(100vh-65px)]">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-tag mb-1">Build Your Squad</div>
          <h1 className="font-display text-4xl md:text-5xl uppercase">Fantasy XI</h1>
        </div>
        <button data-testid="save-fantasy-btn" onClick={save} className="bg-primary-accent text-white px-6 py-3 font-display tracking-widest uppercase hover:bg-[var(--primary-hover)]">
          Save Squad
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="surface-card p-4 accent-bar"><div className="label-tag">Squad</div><div className="font-display text-3xl">{squad.length}/11</div></div>
        <div className="surface-card p-4"><div className="label-tag">Value</div><div className="font-display text-3xl">{totalValue.toFixed(1)}</div></div>
        <div className="surface-card p-4"><div className="label-tag">Budget Left</div><div className={`font-display text-3xl ${budgetLeft < 0 ? "text-primary-accent" : ""}`}>{budgetLeft.toFixed(1)}</div></div>
        <div className="surface-card p-4"><div className="label-tag">Budget Cap</div><div className="font-display text-3xl">{cfg.fantasy_budget}</div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 surface-card p-4">
          <div className="flex gap-2 mb-3 flex-wrap">
            <input
              data-testid="player-search"
              placeholder="Search players..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-zinc-950 border border-zinc-800 px-3 py-2 text-sm focus:border-primary-accent focus:outline-none"
            />
            <div className="flex gap-1">
              {["ALL", ...POSITIONS].map((p) => (
                <button key={p} data-testid={`pos-${p}`} onClick={() => setPosFilter(p)}
                  className={`px-3 py-2 text-xs uppercase tracking-widest border ${posFilter === p ? "border-primary-accent text-primary-accent" : "border-zinc-800 text-zinc-500"}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div className="max-h-[600px] overflow-y-auto divide-y divide-white/5">
            {filtered.map((p) => {
              const t = teams[p.team_id];
              const inSquad = squad.includes(p.player_id);
              return (
                <div key={p.player_id} className="flex items-center justify-between py-2.5" data-testid={`player-${p.player_id}`}>
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-2xl">{t?.flag}</span>
                    <div>
                      <div className="font-display text-base">{p.name}</div>
                      <div className="text-xs text-zinc-500 uppercase tracking-widest">{p.position} · {t?.code}</div>
                    </div>
                  </div>
                  <div className="font-display text-xl mr-3">{p.value.toFixed(1)}</div>
                  <button
                    data-testid={`toggle-${p.player_id}`}
                    onClick={() => togglePlayer(p.player_id)}
                    className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${inSquad ? "bg-primary-accent border-primary-accent text-white" : "border-zinc-800 hover:border-primary-accent"}`}
                  >{inSquad ? "Remove" : "Add"}</button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="surface-card p-4">
          <div className="label-tag mb-2">Your XI</div>
          {POSITIONS.map((pos) => (
            <div key={pos} className="mb-3">
              <div className="text-xs text-zinc-500 uppercase tracking-widest mb-1">{pos}</div>
              {squadPlayers.filter((p) => p.position === pos).map((p) => {
                const t = teams[p.team_id];
                return (
                  <div key={p.player_id} className="flex items-center justify-between border-l-2 border-primary-accent pl-2 py-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span>{t?.flag}</span>
                      <span className="font-display tracking-wider">{p.name}</span>
                    </div>
                    <button
                      data-testid={`captain-${p.player_id}`}
                      onClick={() => setCaptain(captain === p.player_id ? null : p.player_id)}
                      className={captain === p.player_id ? "text-yellow-400" : "text-zinc-600 hover:text-yellow-400"}
                    ><Star size={14} fill={captain === p.player_id ? "currentColor" : "none"}/></button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
